import { useEffect, useState } from "react";
import { Plus, Trash2, Images, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImageUpload from "@/components/ImageUpload";
import BulkGalleryUploadModal from "@/components/BulkGalleryUploadModal";
import api, { formatApiErrorDetail, getFileUrl } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { LANDING_ADMIN } from "@/constants/testIds";

const TYPE_OPTIONS = [
  { value: "single", label: "Imagen única (antes+después ya compuesta)" },
  { value: "pair", label: "Par de fotos (antes y después separadas)" },
  { value: "video", label: "Video" },
];

const EMPTY_FORM = { type: "single", image_url: null, before_url: null, after_url: null, service_id: "none", caption: "" };

/** Card de Ajustes: galeria de resultados (foto unica, par antes/despues o
 * video), asociable a un tratamiento. Independiente de las fotos clinicas
 * de fichas de pacientes: la publicacion es siempre una subida explicita aca. */
export default function GallerySettingsCard() {
  const [items, setItems] = useState([]);
  const [services, setServices] = useState([]);
  const [adding, setAdding] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { toast } = useToast();

  const load = () => api.get("/landing/gallery").then(({ data }) => setItems(data));
  useEffect(() => {
    load();
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);

  const serviceName = (id) => services.find((s) => s.id === id)?.name;

  const handleAdd = async () => {
    if (form.type === "video" && !form.image_url) {
      toast({ title: "Subí el video", variant: "destructive" });
      return;
    }
    if (form.type === "single" && !form.image_url) {
      toast({ title: "Subí la imagen", variant: "destructive" });
      return;
    }
    if (form.type === "pair" && (!form.before_url || !form.after_url)) {
      toast({ title: "Subí ambas fotos (antes y después)", variant: "destructive" });
      return;
    }
    try {
      await api.post("/landing/gallery", {
        image_url: form.type !== "pair" ? form.image_url : null,
        before_url: form.type === "pair" ? form.before_url : null,
        after_url: form.type === "pair" ? form.after_url : null,
        media_type: form.type === "video" ? "video" : "image",
        service_id: form.service_id === "none" ? null : form.service_id,
        caption: form.caption,
      });
      setForm(EMPTY_FORM);
      setAdding(false);
      load();
      toast({ title: "Contenido agregado a la galería" });
    } catch (err) {
      toast({ title: "Error", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await api.delete(`/landing/gallery/${deleteTarget.id}`);
    setDeleteTarget(null);
    load();
  };

  return (
    <div className="bg-white border border-border rounded-xl p-6 sm:p-8 shadow-sm space-y-5" data-testid={LANDING_ADMIN.galleryCard}>
      {/* Fix AI (2026-10-23): en el celular los botones pasan a la linea de abajo en vez de salirse de la pantalla */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl text-foreground">Galería de resultados</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Fotos y videos de resultados reales, nunca fotos de fichas de pacientes. Solo se muestra si activaste el toggle de arriba.
            Recomendado: imágenes JPG/PNG hasta 5MB, videos MP4 hasta 50MB.
          </p>
        </div>
        {!adding && (
          <div className="flex gap-2 shrink-0">
            <Button size="sm" variant="outline" className="gap-2" onClick={() => setBulkOpen(true)} data-testid={LANDING_ADMIN.galleryBulkUploadButton}>
              <UploadCloud className="w-4 h-4" /> Subir en lote
            </Button>
            <Button size="sm" variant="outline" className="gap-2" onClick={() => setAdding(true)} data-testid={LANDING_ADMIN.galleryAddButton}>
              <Plus className="w-4 h-4" /> Nueva
            </Button>
          </div>
        )}
      </div>

      {adding && (
        <div className="border border-border rounded-lg p-4 space-y-3">
          <Select value={form.type} onValueChange={(v) => setForm((f) => ({ ...f, type: v }))}>
            <SelectTrigger data-testid={LANDING_ADMIN.galleryTypeSelect}><SelectValue /></SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>

          {form.type === "single" && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Imagen (antes+después compuesta)</p>
              <ImageUpload currentUrl={form.image_url} onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} placeholderUrl={null} testId={LANDING_ADMIN.gallerySingleUpload} shape="square" />
            </div>
          )}
          {form.type === "pair" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Antes</p>
                <ImageUpload currentUrl={form.before_url} onUploaded={(url) => setForm((f) => ({ ...f, before_url: url }))} placeholderUrl={null} testId={LANDING_ADMIN.galleryBeforeUpload} shape="square" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Después</p>
                <ImageUpload currentUrl={form.after_url} onUploaded={(url) => setForm((f) => ({ ...f, after_url: url }))} placeholderUrl={null} testId={LANDING_ADMIN.galleryAfterUpload} shape="square" />
              </div>
            </div>
          )}
          {form.type === "video" && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Video (mp4)</p>
              <ImageUpload currentUrl={form.image_url} onUploaded={(url) => setForm((f) => ({ ...f, image_url: url }))} placeholderUrl={null} testId={LANDING_ADMIN.galleryVideoUpload} shape="square" mediaType="video" />
            </div>
          )}

          <Select value={form.service_id} onValueChange={(v) => setForm((f) => ({ ...f, service_id: v }))}>
            <SelectTrigger data-testid={LANDING_ADMIN.galleryServiceSelect}><SelectValue placeholder="Asociar a un tratamiento (opcional)" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Sin tratamiento asociado</SelectItem>
              {services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>

          <Input placeholder="Descripción breve (opcional)" value={form.caption} onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))} data-testid={LANDING_ADMIN.galleryCaptionInput} />
          <div className="flex gap-2">
            <Button size="sm" className="bg-brand hover:bg-brand-hover text-white" onClick={handleAdd} data-testid={LANDING_ADMIN.gallerySaveButton}>Guardar</Button>
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setForm(EMPTY_FORM); }} data-testid={LANDING_ADMIN.galleryCancelButton}>Cancelar</Button>
          </div>
        </div>
      )}

      {items.length === 0 && !adding && (
        <div className="text-center py-6">
          <Images className="w-6 h-6 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">Todavía no hay imágenes o videos cargados.</p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {items.map((i) => (
          <div key={i.id} className="border border-border rounded-lg p-2 space-y-2" data-testid={`${LANDING_ADMIN.galleryRowPrefix}${i.id}`}>
            {i.media_type === "video" ? (
              <video src={getFileUrl(i.image_url)} className="w-full aspect-square object-cover rounded" controls />
            ) : i.image_url ? (
              <img src={getFileUrl(i.image_url)} alt="resultado" className="w-full aspect-square object-cover rounded" />
            ) : (
              <div className="grid grid-cols-2 gap-1">
                <img src={getFileUrl(i.before_url)} alt="antes" className="w-full aspect-square object-cover rounded" />
                <img src={getFileUrl(i.after_url)} alt="después" className="w-full aspect-square object-cover rounded" />
              </div>
            )}
            {i.service_id && serviceName(i.service_id) && (
              <p className="text-[11px] font-medium text-brand truncate">{serviceName(i.service_id)}</p>
            )}
            {i.caption && <p className="text-xs text-muted-foreground truncate">{i.caption}</p>}
            <Button variant="ghost" size="sm" className="w-full text-destructive" onClick={() => setDeleteTarget(i)} data-testid={`${LANDING_ADMIN.galleryDeleteButtonPrefix}${i.id}`}>
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
            </Button>
          </div>
        ))}
      </div>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar este contenido?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive hover:bg-destructive/90">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BulkGalleryUploadModal open={bulkOpen} onOpenChange={setBulkOpen} services={services} onDone={load} />
    </div>
  );
}
